import type { ConnectorCapabilities, Mailbox, MailboxStatus, MailProvider } from '@mailpilot/shared';
import { Check, Info, X } from 'lucide-react';
import { Badge, type Tone } from '../ui';

export const PROVIDER_LABELS: Record<MailProvider, string> = {
  gmail: 'Gmail / Google Workspace',
  outlook: 'Outlook / Microsoft 365',
  imap: 'IMAP / SMTP',
  demo: 'Demo mailbox',
};

export const STATUS_META: Record<MailboxStatus, { label: string; tone: Tone }> = {
  connecting: { label: 'Connecting', tone: 'info' },
  active: { label: 'Connected', tone: 'success' },
  needs_reauth: { label: 'Needs reconnection', tone: 'warn' },
  error: { label: 'Error', tone: 'danger' },
  disconnected: { label: 'Disconnected', tone: 'neutral' },
};

/** Connection health derived from status + last sync time. */
export function healthOf(mb: Mailbox): { label: string; tone: Tone } {
  if (mb.status === 'error' || mb.status === 'needs_reauth') return { label: 'Unhealthy', tone: 'danger' };
  if (mb.status === 'disconnected') return { label: 'Not syncing', tone: 'neutral' };
  if (!mb.lastSyncAt) return { label: 'Never synced', tone: 'neutral' };
  const ageMin = (Date.now() - new Date(mb.lastSyncAt).getTime()) / 60_000;
  if (ageMin > 60) return { label: 'Sync delayed', tone: 'warn' };
  return { label: 'Healthy', tone: 'success' };
}

const CAPABILITY_LABELS: { key: Exclude<keyof ConnectorCapabilities, 'limitations'>; label: string }[] = [
  { key: 'pushNotifications', label: 'Instant change notifications' },
  { key: 'incrementalSync', label: 'Incremental sync' },
  { key: 'serverThreads', label: 'Provider conversation threads' },
  { key: 'labels', label: 'Labels (rather than folders)' },
  { key: 'sendAs', label: 'Send through provider API' },
  { key: 'contactsApi', label: 'Provider contacts' },
];

export function CapabilityList({ caps, compact }: { caps: ConnectorCapabilities; compact?: boolean }) {
  return (
    <div className="space-y-3">
      <ul className={compact ? 'grid gap-1.5' : 'grid gap-1.5 sm:grid-cols-2'}>
        {CAPABILITY_LABELS.map((c) => {
          const on = caps[c.key];
          return (
            <li key={c.key} className="flex items-center gap-2 text-[0.8125rem]">
              {on ? <Check className="h-4 w-4 shrink-0 text-success" aria-hidden /> : <X className="h-4 w-4 shrink-0 text-subtle" aria-hidden />}
              <span className={on ? 'text-ink' : 'text-muted'}>
                {c.label}
                <span className="sr-only">{on ? ': supported' : ': not supported'}</span>
              </span>
            </li>
          );
        })}
      </ul>
      {caps.limitations.length > 0 && (
        <div>
          <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted">Limitations</p>
          <ul className="space-y-1">
            {caps.limitations.map((l) => (
              <li key={l} className="flex items-start gap-2 text-[0.8125rem] text-muted">
                <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
                <span>{l}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export function StatusBadges({ mb }: { mb: Mailbox }) {
  const s = STATUS_META[mb.status];
  const h = healthOf(mb);
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {mb.isDemo && <Badge tone="info">DEMO</Badge>}
      <Badge tone={s.tone}>{s.label}</Badge>
      <Badge tone={h.tone}>{h.label}</Badge>
      {mb.isDefaultSender && <Badge tone="primary">Default sender</Badge>}
    </div>
  );
}

/** yyyy-mm-dd for <input type="date"> from an ISO string. */
export function isoToDateInput(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function dateInputToIso(v: string, endOfDay = false): string | null {
  if (!v) return null;
  const d = new Date(`${v}T${endOfDay ? '23:59:59' : '00:00:00'}`);
  return isNaN(d.getTime()) ? null : d.toISOString();
}
